/* eslint-disable react-hooks/exhaustive-deps */
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import toast from "react-hot-toast";

import { theme } from "../../../theme";
import StepFlowNeedsToBeFixed from "../components/needsToBeFixed/StepFlowNeedsToBeFixed";
import FileUploadWrapper from "../../../common/FileUpload/FileUpload";
import FinalOrder from "../components/finalOrder/FinalOrder";
import Error from "./Error";
import {
    customerHaveCard,
    fetchBookAgainDetails,
    fetchCheckIsHaveRunningJobs,
    fetchDescriptionAssets,
    fetchFindFtm,
    uploadImages,
} from "../service";
import { showNotification } from "../../../common/notifications/notifications";
import {
    removeNewJobDetails,
    setCurrentJob,
} from "../../../storage";
import useCustomNavigate from "../../../common/hooks/useCustomNavigate";
import CardTokenizing from "../../../cardTokenizing/CardTokenizing";
import FtmsNotFound from "./FtmsNotFound";
import ConfirmingJob from "../components/confirmingJob/ConfirmingJob";
import BookingConfirmed from "../components/bookingConfirmed/BookingConfirmed";
import DivSpinner from "../../../common/divSpinner";
import { deleteImage } from "../../../common/FileUpload/service";

const StepFlow = ({
    step,
    setStep,
    sections = [],
    skill,
    jobRatePreHour,
    calculateFutureTime,
    range,
    subSkill,
    jobCreate,
    formEditAddressRef,
    setAddressStepSuccessCallback,
    jobGeolocationDetails,
    retryJob,
    isHome,
    confirmBooking,
    runRetryJob,
    bookAgain
}) => {
    const isHaveRetryJob = retryJob > 0;
    const isBookAgain = bookAgain > 0;

    const [jobDescription, setJobDescription] = useState("");
    const [jobDescriptionError, setJobDescriptionError] = useState("");

    const [error, setError] = useState("");
    const [jobCreating, setJobCreating] = useState(false);
    const [isOpenCardModal, setIsOpenCardModal] = useState(false);
    const [isOpenFtmsNotFound, setIsOpenFtmsNotFound] = useState(false);
    const [isRetry, setIsRetry] = useState(isHaveRetryJob);

    const [isLoading, setIsLoading] = useState(false);

    const deleteJobImage = async (image_id) => {
        await deleteImage({job_id: bookAgain, image_id});
    }

    const { files, setFiles, FileUpload } = FileUploadWrapper(true, deleteJobImage);
    const { navigateToCustom } = useCustomNavigate();

    const currentSection = useMemo(
        () => sections?.[step - 1]?.component,
        [sections, step]
    );

    const handleOpenFtmNotFound = () => setIsOpenFtmsNotFound(true);
    const handleCloseFtmNotFound = () => setIsOpenFtmsNotFound(false);

    const toggleCardModal = useCallback(() => {
        setIsOpenCardModal((prev) => !prev);
    }, []);

    useEffect(() => {
        const jobId = isHaveRetryJob ? retryJob : isBookAgain ? bookAgain : null;
        const fetchDetails = isHaveRetryJob ? fetchDescriptionAssets : fetchBookAgainDetails;

        if (!jobId) return undefined;

        let isActive = true;

        const loadJobDescription = async () => {
            setIsLoading(true);
            try {
                const response = await fetchDetails(jobId);
                if (!isActive) return;

                if (response) {
                    setJobDescription(response?.data?.job_description ?? "");

                    // Book-again images are already hosted, so use their URL as the
                    // upload preview instead of trying to create an object URL.
                    if (isBookAgain) {
                        setFiles(
                            (response?.data?.job_images ?? []).map((image) => ({
                                file: null,
                                preview: image.job_image_path,
                                isExisting: true,
                                id: image.job_image_id,
                            }))
                        );
                    }
                } else {
                    toast.error("Failed to load job description.");
                }
            } catch (requestError) {
                if (isActive) {
                    toast.error("An error occurred while loading job description.");
                }
            } finally {
                if (isActive) setIsLoading(false);
            }
        };

        loadJobDescription();

        return () => {
            isActive = false;
        };
    }, [bookAgain, isBookAgain, isHaveRetryJob, retryJob, setFiles]);

    const validateStep = useCallback(() => {
        switch (step) {
            case 1:
                if (!skill?.skill_id || !subSkill?.sub_skill_id) {
                    return "Please select service.";
                }
                break;

            case 3:
                if (!jobGeolocationDetails) {
                    return "Please select job location.";
                }
                break;

            case 5:
                if (!jobDescription.trim()) {
                    setJobDescriptionError(
                        "Please add job description."
                    );

                    return "Please add job description.";
                }

                setJobDescriptionError("");
                break;

            default:
                break;
        }

        return null;
    }, [
        step,
        skill?.skill_id,
        subSkill?.sub_skill_id,
        jobGeolocationDetails,
        jobDescription,
    ]);

    const handleStepUp = useCallback(() => {
        const validationError = validateStep();
        if (validationError) {
            setError(validationError);
            return false;
        }

        setError("");
        setStep((prev) => prev + 1);

        return true;
    }, [setStep, validateStep]);

    const handleStepDown = useCallback(() => {
        setError("");
        if(step === 6 && isRetry){
            setStep(4);
            return;
        }

        if(step === 5 && isRetry){
            setStep(3);
            return;
        }

        if(step === 3 && isRetry){
            setStep(1);
            navigateToCustom(`/home`);
            return;
        }

        setStep((prev) => Math.max(prev - 1, 1));
    }, [isRetry, navigateToCustom, step]);

    const searchFtm = useCallback(
        async (jobId) => {
            try {
                const response = await fetchFindFtm(jobId);
                if (response?.status !== "Success") {
                    toast.error(
                        "Failed to find FTM."
                    );
                    return;
                }

                if (response?.is_send_notifications === 1) {
                    navigateToCustom(
                        `/current/job/${jobId}`
                    );
                } else {
                    setStep(5);
                    handleOpenFtmNotFound();
                }
            } catch (error) {
                setStep(5);
                toast.error(
                    "Error searching FTM."
                );
            }
        },
        [navigateToCustom, setStep]
    );

    const checkCustomerHaveCard = useCallback(async () => {
        try {
            const response = await customerHaveCard();
            if (response?.is_have_card) return true;

            setIsOpenCardModal(true);

            return false;
        } catch (error) {
            toast.error(
                "Error checking pre-authorization."
            );

            return false;
        }
    }, []);

    const makeNewJob = useCallback(async () => {
        if (jobCreating) return;

        try {
            setJobCreating(true);
            setStep(6);

            const hasCard = await checkCustomerHaveCard();
            if (!hasCard) return;

            const runningJobs = await fetchCheckIsHaveRunningJobs();
            if (runningJobs?.is_have_running_jobs === 1) {
                showNotification({
                    notificationType: "CREATE_JOB_HAVE_RUNNING_JOBS",
                });

                setStep(5);
                return;
            }

            const response = await jobCreate({
                description: jobDescription,
                creditNoteCode: "",
            });

            if (response?.status !== "Success") {
                toast.error(
                    "Failed to create the job."
                );

                setStep(5);
                return;
            }

            const {
                new_Job_id,
                job_firebase_document_id,
            } = response;

            setCurrentJob({
                job_id: new_Job_id,
                job_firebase_document_id,
            });

            if (files?.length) {
                await Promise.all(
                    files
                        .filter((file) => !file.isExisting && file.file)
                        .map((file) => uploadImages(new_Job_id, file.file))
                );
            }

            removeNewJobDetails();

            setStep(7);
            await new Promise((resolve) => setTimeout(resolve, 500));
            await searchFtm(new_Job_id);
        } catch (error) {
            toast.error(
                "An error occurred while creating the job."
            );
        } finally {
            setJobCreating(false);
        }
    }, [
        checkCustomerHaveCard,
        jobCreate,
        jobDescription,
        files,
        jobCreating,
        searchFtm,
        setStep,
    ]);

    const handleContinue = useCallback(async () => {
        if(step === 4 && !isHome){
            confirmBooking();
            return;
        }

        if(step === 4 && isRetry){
            setStep(6);
            return;
        }

        if (step === 3 && formEditAddressRef?.current?.submitForm) {
            await formEditAddressRef.current.submitForm();
            return;
        }

        if(step === 6 && isRetry){
            await runRetryJob();
            return;
        }

        if (step === 6) {
            await makeNewJob();
            return;
        }

        handleStepUp();
    }, [step, isRetry, isHome, confirmBooking, formEditAddressRef, handleStepUp, makeNewJob, runRetryJob, setStep]);

    useEffect(() => {
        if (setAddressStepSuccessCallback) {
            setAddressStepSuccessCallback(() => handleStepUp);
        }
    }, [
        setAddressStepSuccessCallback,
        handleStepUp,
    ]);

    const widenArrivalWindow = () => {
        setStep(3);
        handleCloseFtmNotFound();
        setIsRetry(true);
    };

    if(isLoading){
        return <DivSpinner/>;
    }

    return (
        <>
            <div className="card mb-3 mt-lg-0 h-100">
                <div
                    className="card-body pb-0"
                    style={
                        !isHome ? {
                            minHeight: "700px",
                            maxHeight: "900px",
                        }: undefined
                    }
                >
                    {step < sections.length && currentSection}
                    {step === 5 && (
                        <StepFlowNeedsToBeFixed
                            jobDescription={jobDescription}
                            jobDescriptionError={jobDescriptionError}
                            setJobDescription={setJobDescription}
                            FileUpload={FileUpload}
                        />
                    )}
                    {step === 6 && (
                        <FinalOrder
                            files={files}
                            jobDescription={jobDescription}
                            jobGeolocationDetails={jobGeolocationDetails}
                            skill={skill}
                            subSkill={subSkill}
                            range={range}
                            calculateFutureTime={calculateFutureTime}
                            jobRatePreHour={jobRatePreHour}
                            jobCreate={jobCreate}
                            jobCreating={jobCreating}
                            setJobCreating={setJobCreating}
                        />
                    )}
                    {step === 7 && <ConfirmingJob/>}
                    {step === 8 && <BookingConfirmed/>}
                </div>

                <div className="card-footer mt-0 pt-0">
                    {error && (
                        <Error
                            message={error}
                        />
                    )}

                    {step < 7 && (
                        <div className="row g-2">
                            <div className="col-6">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm w-100 my-0"
                                    onClick={handleStepDown}
                                    disabled={step === 1 || (isRetry && step === 4) || jobCreating}
                                >
                                    {(isRetry && step === 3) ? "Reset" : "Back"} {step}
                                </button>
                            </div>

                            <div className="col-6">
                                <button
                                    type="button"
                                    className={`btn btn-${theme.main} btn-sm w-100 my-0`}
                                    onClick={handleContinue}
                                    disabled={jobCreating}
                                >
                                    {jobCreating ? "Processing..." : step === 6 ? "Confirm & Pay" : "Continue"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <CardTokenizing
                show={isOpenCardModal}
                closeModal={toggleCardModal}
            />

            <FtmsNotFound open={isOpenFtmsNotFound} handleClose={handleCloseFtmNotFound} widenArrivalWindow={widenArrivalWindow}/>
        </>
    );
};

export default StepFlow;
